/**
 * Embeds / extracts the video URL inside the post content HTML string.
 * The video URL is stored as an HTML comment at the end of the content:
 *   <!-- @video: https%3A%2F%2F... -->
 *
 * DOMPurify strips HTML comments automatically, so nothing leaks to the
 * public site. TipTap's ProseMirror parser also ignores HTML comments,
 * keeping the editor view clean.
 */

const MARKER_START = "<!-- @video: ";
const MARKER_END   = " -->";

/** Appends the video URL marker to the content string. */
export function embedVideoInContent(content: string, videoUrl: string): string {
  const clean = stripVideoFromContent(content);
  if (!videoUrl.trim()) return clean;
  return clean + MARKER_START + encodeURIComponent(videoUrl.trim()) + MARKER_END;
}

/** Returns the clean content (marker removed) and the extracted video URL. */
export function extractVideoFromContent(content: string): { clean: string; videoUrl: string } {
  if (!content) return { clean: "", videoUrl: "" };
  const idx = content.lastIndexOf(MARKER_START);
  if (idx === -1) return { clean: content, videoUrl: "" };
  const endIdx = content.indexOf(MARKER_END, idx);
  if (endIdx === -1) return { clean: content, videoUrl: "" };
  try {
    const encoded = content.slice(idx + MARKER_START.length, endIdx);
    const videoUrl = decodeURIComponent(encoded);
    const clean = content.slice(0, idx);
    return { clean, videoUrl };
  } catch {
    return { clean: content, videoUrl: "" };
  }
}

/** Returns only the clean content, with the video marker removed. */
export function stripVideoFromContent(content: string): string {
  return extractVideoFromContent(content).clean;
}
